import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Link as LinkIcon,
  BarChart3,
  Zap,
  Smartphone,
  Code,
  User,
  BookOpen,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Check,
  Globe,
  Shield,
  MousePointerClick,
  QrCode,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { billingAPI } from '../services/api';

const Hero = () => (
  <section className="relative aurora-bg overflow-hidden rounded-3xl border border-ink-200 bg-white/70 backdrop-blur-sm">
    <div className="bg-grid absolute inset-0 opacity-60 pointer-events-none" />
    <div className="relative px-6 sm:px-10 lg:px-16 pt-16 sm:pt-20 pb-14 sm:pb-16 text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white/90 px-3 py-1 text-xs font-medium text-ink-600 shadow-soft">
        <Sparkles className="h-3.5 w-3.5 text-brand-500" />
        Drop-in replacement for Firebase Dynamic Links
      </div>

      <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-ink-900 leading-[1.05]">
        Short links.
        <br className="hidden sm:block" />{' '}
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-600 via-violet-600 to-fuchsia-600">
          Deep analytics.
        </span>
      </h1>

      <p className="mt-5 max-w-2xl mx-auto text-lg text-ink-600 leading-relaxed">
        Create branded short links, route users into your mobile apps, and
        understand exactly who's clicking — all in one place.
      </p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/register"
          className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-5 py-3 text-sm font-medium text-white shadow-soft hover:bg-ink-800 hover:shadow-pop transition"
        >
          Start for free
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/api-docs"
          className="inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-5 py-3 text-sm font-medium text-ink-700 hover:bg-ink-50 transition"
        >
          <BookOpen className="h-4 w-4" />
          Read the API docs
        </Link>
      </div>

      {/* Mock shortener input — purely decorative on the landing page */}
      <div className="mt-12 max-w-2xl mx-auto">
        <div className="rounded-2xl border border-ink-200 bg-white shadow-card p-2 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <input
              type="text"
              defaultValue="https://example.com/my-very-long-marketing-page?utm=launch"
              className="w-full rounded-lg border-0 pl-10 pr-3 py-3 text-sm text-ink-800 placeholder-ink-400 focus:outline-none"
              readOnly
            />
          </div>
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-brand-500 to-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-soft hover:shadow-pop transition"
          >
            Shorten link
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="mt-3 text-xs text-ink-500">
          Free during beta · No credit card required
        </p>
      </div>
    </div>
  </section>
);

const StatRow = () => (
  <section className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px rounded-2xl border border-ink-200 bg-ink-100 overflow-hidden shadow-soft">
    {[
      { label: 'Avg redirect', value: '< 40ms' },
      { label: 'Uptime', value: '99.99%' },
      { label: 'Platforms', value: 'iOS · Android · Web' },
      { label: 'Links live', value: '∞' },
    ].map((s) => (
      <div key={s.label} className="bg-white px-5 py-5 text-center">
        <p className="text-2xl font-bold tracking-tight text-ink-900">
          {s.value}
        </p>
        <p className="mt-1 text-xs uppercase tracking-wider text-ink-500">
          {s.label}
        </p>
      </div>
    ))}
  </section>
);

const featureItems = [
  {
    icon: Zap,
    title: 'Instant redirects',
    body: 'Edge-cached short links with sub-50ms redirect times worldwide.',
    accent: 'from-amber-400 to-orange-500',
  },
  {
    icon: Smartphone,
    title: 'Smart deep linking',
    body: 'Route users into your iOS and Android apps, fall back gracefully to web.',
    accent: 'from-emerald-400 to-teal-500',
  },
  {
    icon: BarChart3,
    title: 'Real analytics',
    body: 'Clicks, unique visitors, devices, geos, referrers — broken down by link.',
    accent: 'from-brand-500 to-violet-600',
  },
  {
    icon: QrCode,
    title: 'QR codes built-in',
    body: 'Every short link comes with a styled, scannable QR code you can export.',
    accent: 'from-pink-500 to-rose-500',
  },
  {
    icon: Code,
    title: 'Full REST API',
    body: 'Create, manage and track links from your backend with API keys.',
    accent: 'from-sky-500 to-indigo-500',
  },
  {
    icon: Shield,
    title: 'Yours to control',
    body: 'Self-hostable, with role-based admin and rate-limiting baked in.',
    accent: 'from-slate-700 to-slate-900',
  },
];

const FeatureGrid = () => (
  <section id="features" className="mt-20">
    <div className="text-center max-w-2xl mx-auto">
      <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase">
        Everything you need
      </p>
      <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-ink-900">
        A modern link platform, without the SaaS bloat
      </h2>
      <p className="mt-3 text-ink-600">
        Built to replace Firebase Dynamic Links — and then some.
      </p>
    </div>

    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {featureItems.map(({ icon: Icon, title, body, accent }) => (
        <div
          key={title}
          className="group relative rounded-2xl border border-ink-200 bg-white p-6 shadow-soft transition hover:shadow-pop hover:-translate-y-0.5"
        >
          <div
            className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-soft`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-ink-900 tracking-tight">
            {title}
          </h3>
          <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="mt-20 rounded-3xl border border-ink-200 bg-white p-8 sm:p-12 shadow-card">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase">
          How it works
        </p>
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-ink-900">
          From URL to insight in three steps
        </h2>
        <ul className="mt-6 space-y-4">
          {[
            {
              title: 'Create a link',
              body: 'Paste a destination, configure iOS/Android fallbacks, set an expiry.',
            },
            {
              title: 'Share anywhere',
              body: 'Send your dyn.link URL or QR code over social, email, or print.',
            },
            {
              title: 'See it perform',
              body: 'Live click stream, geo & device breakdowns, per-link analytics.',
            },
          ].map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <div className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-brand-700 text-sm font-semibold ring-1 ring-brand-200">
                {i + 1}
              </div>
              <div>
                <p className="text-sm font-semibold text-ink-900">
                  {step.title}
                </p>
                <p className="text-sm text-ink-600">{step.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Visual mock */}
      <div className="relative">
        <div className="absolute inset-0 -m-3 rounded-3xl bg-gradient-to-tr from-brand-200/50 via-fuchsia-200/40 to-transparent blur-2xl" />
        <div className="relative rounded-2xl border border-ink-200 bg-ink-900 text-ink-100 p-5 shadow-pop font-mono text-xs">
          <div className="flex items-center gap-1.5 mb-3">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
            <span className="ml-2 text-ink-400">POST /api/links</span>
          </div>
          <pre className="whitespace-pre-wrap leading-relaxed">
{`{
  "destination": "https://example.com/launch",
  "ios": { "bundleId": "com.example.app" },
  "android": { "package": "com.example.app" },
  "expiresIn": "30d"
}`}
          </pre>
          <div className="mt-4 border-t border-white/10 pt-3 text-emerald-300">
            <span className="opacity-60">→</span>{' '}
            <span className="text-white">https://dyn.link/aX9k</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Public pricing — sourced from the same admin-managed catalog the billing page
// uses (GET /billing/plans), so super-admin edits show up here immediately.
const fmtQuota = (n, noun) => (n === null ? `Unlimited ${noun}` : `${n} ${noun}`);

const Pricing = () => {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    let active = true;
    billingAPI
      .getPlans()
      .then((res) => {
        if (active && res.success) setPlans(res.data.plans || []);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Hide the whole section until the catalog loads (avoids a layout flash).
  if (!plans || plans.length === 0) return null;

  return (
    <section id="pricing" className="mt-20">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase">
          Pricing
        </p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-ink-900">
          Simple plans that scale with you
        </h2>
        <p className="mt-3 text-ink-600">
          Start free. Upgrade when you need higher limits — cancel anytime.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
        {plans.map((plan) => (
          <div
            key={plan.key}
            className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-soft ${
              plan.isHighlighted ? 'border-brand-300 ring-1 ring-brand-200' : 'border-ink-200'
            }`}
          >
            {plan.isHighlighted && (
              <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-brand-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                <Sparkles className="h-3 w-3" /> Popular
              </span>
            )}

            <h3 className="text-lg font-bold text-ink-900">{plan.label}</h3>
            {plan.tagline && <p className="mt-1 text-sm text-ink-500">{plan.tagline}</p>}

            <div className="mt-4 flex items-end gap-1">
              {plan.paid ? (
                <>
                  <span className="text-3xl font-bold text-ink-900">${plan.price.usd}</span>
                  <span className="pb-1 text-sm text-ink-500">/mo</span>
                </>
              ) : (
                <span className="text-3xl font-bold text-ink-900">Free</span>
              )}
            </div>
            {plan.paid && (
              <p className="mt-0.5 text-xs text-ink-400">
                or ₹{plan.price.inr.toLocaleString('en-IN')}/mo via Razorpay
              </p>
            )}

            <ul className="mt-5 space-y-2 text-sm text-ink-700">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                {fmtQuota(plan.quotas.maxLinks, 'links')}
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                {fmtQuota(plan.quotas.maxProfilePages, 'profile pages')}
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                {fmtQuota(plan.quotas.maxWebhooks, 'webhooks')}
              </li>
            </ul>

            <Link
              to={plan.paid ? '/pricing' : '/register'}
              className={`mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition ${
                plan.isHighlighted
                  ? 'bg-gradient-to-b from-brand-500 to-brand-600 text-white shadow-soft hover:shadow-pop'
                  : 'border border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
              }`}
            >
              {plan.paid ? `Choose ${plan.label}` : 'Start for free'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ))}
      </div>

      <p className="mt-6 text-center text-sm text-ink-500">
        Need the full comparison?{' '}
        <Link to="/pricing" className="font-medium text-brand-600 hover:text-brand-700">
          See all plans &amp; pricing
        </Link>
      </p>
    </section>
  );
};

const CTA = () => (
  <section className="mt-20 relative overflow-hidden rounded-3xl border border-ink-900 bg-ink-900 text-white p-10 sm:p-14 text-center shadow-pop">
    <div
      className="absolute inset-0 opacity-30 pointer-events-none"
      style={{
        backgroundImage:
          'radial-gradient(600px circle at 20% 20%, rgba(99,102,241,0.6), transparent 60%), radial-gradient(500px circle at 80% 80%, rgba(236,72,153,0.5), transparent 60%)',
      }}
    />
    <div className="relative">
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
        Ready to ship better links?
      </h2>
      <p className="mt-3 text-ink-300 max-w-xl mx-auto">
        Set up your account in under a minute. Bring your existing domain when
        you're ready.
      </p>
      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/register"
          className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-semibold text-ink-900 hover:bg-ink-100 transition"
        >
          Create your account
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/api-docs"
          className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-medium text-white hover:bg-white/10 transition"
        >
          Browse API docs
        </Link>
      </div>
    </div>
  </section>
);

const AuthenticatedHero = ({ user }) => {
  const quickActions = [
    {
      to: '/create',
      icon: LinkIcon,
      title: 'Create a link',
      body: 'Shorten a URL with mobile deep-linking.',
      accent: 'from-brand-500 to-violet-600',
    },
    {
      to: '/links',
      icon: BarChart3,
      title: 'Your links',
      body: 'Search, edit and track every link you own.',
      accent: 'from-emerald-500 to-teal-600',
    },
    {
      to: '/profile-pages',
      icon: MousePointerClick,
      title: 'Profile pages',
      body: 'Build a link-in-bio page for your audience.',
      accent: 'from-pink-500 to-rose-500',
    },
    {
      to: '/api-keys',
      icon: Code,
      title: 'API keys',
      body: 'Generate keys to automate from your backend.',
      accent: 'from-slate-700 to-ink-900',
    },
  ];

  return (
    <>
      <section className="relative aurora-bg overflow-hidden rounded-3xl border border-ink-200 bg-white/70 backdrop-blur-sm px-6 sm:px-10 py-10 sm:py-12">
        <div className="bg-grid absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative">
          <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase">
            Welcome back
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-ink-900">
            Hi, {user?.username || 'there'} 👋
          </h1>
          <p className="mt-2 text-ink-600 max-w-xl">
            Pick up where you left off — create a new link, dig into analytics,
            or fine-tune your profile pages.
          </p>

          <div className="mt-7 flex flex-wrap gap-2">
            <Link
              to="/create"
              className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-medium text-white shadow-soft hover:bg-ink-800 transition"
            >
              <LinkIcon className="h-4 w-4" />
              Create new link
            </Link>
            <Link
              to="/links"
              className="inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50 transition"
            >
              View my links
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {quickActions.map(({ to, icon: Icon, title, body, accent }) => (
          <Link
            key={to}
            to={to}
            className="group rounded-2xl border border-ink-200 bg-white p-5 shadow-soft hover:shadow-pop hover:-translate-y-0.5 transition"
          >
            <div
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-soft`}
            >
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-ink-900 tracking-tight">
              {title}
            </h3>
            <p className="mt-1 text-sm text-ink-500 leading-relaxed">{body}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-600 group-hover:gap-2 transition-all">
              Open <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        ))}
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
          <h3 className="text-sm font-semibold text-ink-900">
            Get the most out of Dynalink
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-ink-700">
            {[
              'Use UTM params on your destination URLs for richer analytics.',
              'Set iOS bundle IDs and Android packages on links to enable deep linking.',
              'Generate an API key to create links from your backend or CI.',
              'Build a profile page to consolidate all your links in one place.',
            ].map((tip) => (
              <li key={tip} className="flex gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-ink-200 bg-gradient-to-br from-ink-900 to-ink-800 text-white p-6 shadow-soft">
          <BookOpen className="h-6 w-6 text-brand-300" />
          <h3 className="mt-4 text-base font-semibold tracking-tight">
            Browse the API docs
          </h3>
          <p className="mt-1.5 text-sm text-ink-300">
            Programmatic access to every link, profile and analytic event.
          </p>
          <Link
            to="/api-docs"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-300 hover:text-brand-200"
          >
            Open docs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

const Home = () => {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    return (
      <div className="max-w-6xl mx-auto">
        <AuthenticatedHero user={user} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <Hero />
      <StatRow />
      <FeatureGrid />
      <HowItWorks />
      <Pricing />
      <CTA />
    </div>
  );
};

export default Home;
